<script lang="ts">
import {
  AUTH_API_INJECTION_TOKEN,
  ACCOUNTS_API_INJECTION_TOKEN,
  USERS_API_INJECTION_TOKEN,
  APP_CONFIG,
  ExpirationOverlay,
} from '@onereach/auth-ui-module';
import { MIN_ZINDEX, OrToastContainerV3 } from '@onereach/ui-components';
import { storeToRefs } from 'pinia';
import { defineComponent, provide, ref, onErrorCaptured, watch } from 'vue';

import AccountsApi from '@/api/AccountsApi';
import AuthApi from '@/api/Auth';
import UsersApi from '@/api/UsersApi';
import { AppPage } from '@/components';
import { useI18n } from '@/composables';
import config from '@/config';
import { TranslatableError } from '@/errors';
import SentryApi from '@/sentry';
import { useAuth, useCcpStore } from '@/stores';

import pkg from '../package.json';

import { usePermissionsStore } from './stores/permissions';


export default defineComponent({
  components: {
    ExpirationOverlay,
    AppPage,
    OrToastContainerV3,
  },
  setup() {
    const authStore = useAuth();
    const permissionsStore = usePermissionsStore();
    const showError = ref(false);
    const errorText = ref('');
    const lockScreenUsername = ref('');
    const loading = ref(true);

    const { t } = useI18n();
    const {
      authorized: isAuthorized,
      username,
      identityProvider,
      reactiveToken: token,
      cookiesAnotherUser,
      cookiesNoRights,
      cookiesExpired,
      userId,
      multiUserId,
      accountId,
      role,
    } = storeToRefs(authStore);

    const cppStore = useCcpStore();
    const { internalMode, customAppName } = storeToRefs(cppStore);
    //provide api services to AuthPopover
    provide(AUTH_API_INJECTION_TOKEN, AuthApi);
    provide(ACCOUNTS_API_INJECTION_TOKEN, AccountsApi);
    provide(USERS_API_INJECTION_TOKEN, UsersApi);
    provide(APP_CONFIG, config);

    provide(MIN_ZINDEX, 20);

    onErrorCaptured((e) => {
      if (e instanceof TranslatableError) {
        // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
        errorText.value = t(e.translationKey);
        SentryApi.captureException(e.innerError);
      } else {
        errorText.value = t('errors.commonText');
        SentryApi.captureException(e);
      }
      showError.value = true;
      return false;
    });

    watch(isAuthorized, async (val) => {
      if (!val) return;
      loading.value = true;
      try {
        await permissionsStore.getCurrentPermissions();
        lockScreenUsername.value = await authStore.getUserNameForLockScreen();
        await Promise.all([
          cppStore.getSkus(),
          cppStore.getCustomers(),
          cppStore.getMapping(),
          cppStore.getCategories(),
        ]);
      } finally {
        loading.value = false;
      }
    });

    return {
      authStore,
      isAuthorized,
      multiUserId,
      cookiesAnotherUser,
      cookiesNoRights,
      username,
      accountId,
      role,
      authUrl: config.AUTH_UI_URL,
      identityProvider,
      token,
      userId,
      showError,
      cookiesExpired,
      errorText,
      permissionsStore,
      lockScreenUsername,
      serviceTranslations: pkg.projectConfig.translations,
      config,
      internalMode,
      customAppName,
      loading,
    };
  },
});
</script>

<template>
  <div class="app h-full">
    <expiration-overlay
      :lock-username="lockScreenUsername"
      :username="username!"
      :auth-url="authUrl"
      :identity-provider="identityProvider"
      :token="token!"
      :user-id="userId!"
      :account-id="accountId!"
      :cookies-expired="cookiesExpired"
      :cookies-another-user="cookiesAnotherUser"
      :cookies-no-rights="cookiesNoRights"
      :no-access="permissionsStore.noAccess"
      :login-overlay-disabled="false"
      @on-update-cookie-expiration="authStore.updateCookieExpiration"
    />
    <AppPage
      :account-id="accountId!"
      :user-role="role"
      :loading="loading"
      :token="token!"
      :config="config"
      :internal-mode="internalMode"
      :service-translations="serviceTranslations"
      :error-text="errorText"
      :show-error="showError"
      :custom-app-name="customAppName as string"
      data-test="ccp-main-page"
      @update:show-error="showError = $event"
    >
      <OrToastContainerV3 />
      <!-- eslint-disable-next-line vue/no-undef-components -->
      <RouterView />
    </AppPage>
  </div>
</template>

<style lang="scss">
html,
body,
#app,
.app {
  height: 100%;
}
</style>
